/**
 * 12-hour (AM/PM) <-> 24-hour "HH:mm" conversion for the Date Filters time
 * selector.
 *
 * The WIRE format is always 24-hour "HH:mm" in IST — it sorts, validates, and
 * parses without locale ambiguity, and it's exactly what the server's
 * `parseIstTimeOfDay` expects. AM/PM exists only in the UI, and is built here
 * rather than left to `<input type="time">`, whose 12h-vs-24h rendering follows
 * the browser locale and would show 24h to anyone not on a US locale.
 */

export type Meridiem = "AM" | "PM";

export interface Time12 {
  /** 1-12 */
  hour: number;
  /** 0-59 */
  minute: number;
  meridiem: Meridiem;
}

/** Whole-day bounds, used when the time toggle is switched on. */
export const DAY_START_24 = "00:00";
export const DAY_END_24 = "23:59";

/** "14:30" → { hour: 2, minute: 30, meridiem: "PM" }. Null if unparseable. */
export function parse24(value: string | null | undefined): Time12 | null {
  if (typeof value !== "string") return null;
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) return null;

  const h24 = Number(match[1]);
  const minute = Number(match[2]);
  if (h24 < 0 || h24 > 23 || minute < 0 || minute > 59) return null;

  const meridiem: Meridiem = h24 < 12 ? "AM" : "PM";
  // 0 → 12 AM, 12 → 12 PM, 13 → 1 PM.
  const hour = h24 % 12 === 0 ? 12 : h24 % 12;
  return { hour, minute, meridiem };
}

/** { hour: 2, minute: 30, meridiem: "PM" } → "14:30". */
export function to24({ hour, minute, meridiem }: Time12): string {
  const base = hour % 12; // 12 AM → 0, 12 PM → 0 (+12 below)
  const h24 = meridiem === "PM" ? base + 12 : base;
  return `${String(h24).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

/** "14:30" → "2:30 PM". Returns "" when unparseable, so callers can skip it. */
export function format12(value: string | null | undefined): string {
  const t = parse24(value);
  if (!t) return "";
  return `${t.hour}:${String(t.minute).padStart(2, "0")} ${t.meridiem}`;
}

/**
 * True when the pair narrows nothing — i.e. it spans the entire day. Used to
 * keep inert params off the URL and out of the applied-filter summary.
 */
export function isWholeDay(
  start: string | null | undefined,
  end: string | null | undefined,
): boolean {
  return (
    (!start?.trim() || start === DAY_START_24) &&
    (!end?.trim() || end === DAY_END_24)
  );
}

/**
 * True when the window is impossible: same-day range whose end time precedes
 * its start time. Multi-day ranges are always valid — the times are the
 * endpoints of ONE continuous window, so "2 PM on the 29th → 10 AM on the 31st"
 * is perfectly ordered.
 */
export function isInvertedSameDay(
  startDate: string,
  endDate: string,
  startTime: string,
  endTime: string,
): boolean {
  if (!startDate || !endDate || startDate !== endDate) return false;
  const s = parse24(startTime);
  const e = parse24(endTime);
  if (!s || !e) return false;
  return to24(s) > to24(e);
}
